import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, PlayCircle, RefreshCw, Trash2, ImageIcon, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Admin panel to pre-generate concept illustrations for the top ~62 core
 * Pre-K/K concepts. Also lets admins review + delete cached images.
 */
export default function ConceptImageSeedPage() {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [images, setImages] = useState([]);
  const [totalCached, setTotalCached] = useState(0);
  const [starting, setStarting] = useState(false);
  const [q, setQ] = useState('');

  const backendBase = process.env.REACT_APP_BACKEND_URL || '';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/admin/concept-images/seed-status');
      setStatus(res.data);
    } catch (err) { void err; }
  }, [api]);

  const fetchImages = useCallback(async () => {
    try {
      const res = await api.get('/admin/concept-images?limit=200');
      setImages(res.data.items || []);
      setTotalCached(res.data.total || 0);
    } catch (err) { void err; }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    fetchImages();
    const timer = setInterval(() => {
      if (status?.running) {
        fetchStatus();
        fetchImages();
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [status?.running, fetchStatus, fetchImages]);

  const startSeed = async () => {
    setStarting(true);
    try {
      const res = await api.post('/admin/concept-images/seed');
      if (res.data.started) {
        toast.success(`Seeding ${res.data.total} concept illustrations…`);
      } else {
        toast.info('Seed job is already running');
      }
      fetchStatus();
    } catch (err) {
      toast.error('Could not start seed job');
    } finally {
      setStarting(false);
    }
  };

  const removeImage = async (cacheKey) => {
    if (!window.confirm('Delete this cached illustration? A new one will be generated on next view.')) return;
    try {
      await api.delete(`/admin/concept-images/${cacheKey}`);
      toast.success('Deleted');
      fetchImages();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const percent = status && status.total ? Math.round((status.done / status.total) * 100) : 0;
  const isRunning = !!status?.running;

  const filtered = images.filter((img) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (img.concept || '').toLowerCase().includes(needle) || (img.subject || '').toLowerCase().includes(needle);
  });

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="concept-image-seed-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-fredoka font-bold text-slate-800" data-testid="seed-title">Concept Illustrations</h1>
        <p className="text-sm text-slate-500 mt-1">Pre-generate pictures for the top ~62 Pre-K &amp; K concepts so children see them instantly instead of waiting ~15s on first view.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <PlayCircle className="w-5 h-5 text-emerald-500" />
            Seed Job
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={startSeed}
              disabled={isRunning || starting}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="start-seed-btn"
            >
              {isRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
              {isRunning ? 'Seeding in progress…' : 'Start seed job'}
            </Button>
            <Button variant="outline" onClick={() => { fetchStatus(); fetchImages(); }} data-testid="refresh-status-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <span className="text-sm text-slate-500">
              Corpus: <strong>{status?.corpus_total ?? '—'}</strong>
              {' · '}Currently cached: <strong>{totalCached}</strong>
            </span>
          </div>

          {status && status.total > 0 && (
            <div className="space-y-2" data-testid="seed-progress-block">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">
                  {status.done} of {status.total} processed
                  {status.cached_hits > 0 && ` · ${status.cached_hits} were already cached`}
                  {status.errors > 0 && ` · ${status.errors} errors`}
                </span>
                <span className="font-semibold text-slate-700">{percent}%</span>
              </div>
              <Progress value={percent} className="h-3" />
              {isRunning && status.current && (
                <p className="text-xs text-slate-500">
                  Working on: <span className="font-medium">{status.current}</span>
                </p>
              )}
              {!isRunning && status.finished_at && (
                <p className="text-xs text-emerald-700">
                  Finished {new Date(status.finished_at).toLocaleString()}
                </p>
              )}
              {status.last_error && (
                <p className="text-xs text-amber-700 truncate">Last error: {status.last_error}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ImageIcon className="w-5 h-5 text-blue-500" />
            Cached illustrations ({totalCached})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 relative max-w-sm">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by concept or subject…"
              className="pl-9"
              data-testid="seed-search-input"
            />
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No cached illustrations{q ? ' match your search' : ' yet'}.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="seed-image-grid">
              {filtered.map((img) => {
                const src = img.image_url?.startsWith('http') ? img.image_url : `${backendBase}${img.image_url}`;
                return (
                  <div key={img.cache_key} className="relative group border rounded-xl overflow-hidden bg-white">
                    <div className="aspect-square bg-gradient-to-br from-slate-50 to-white">
                      <img src={src} alt={img.concept} loading="lazy" className="w-full h-full object-contain" />
                    </div>
                    <div className="p-2 text-xs">
                      <p className="font-semibold text-slate-700 truncate" title={img.concept}>{img.concept}</p>
                      <div className="flex items-center justify-between mt-1">
                        <Badge variant="outline" className="text-[10px] capitalize">{img.subject}</Badge>
                        <button
                          type="button"
                          onClick={() => removeImage(img.cache_key)}
                          className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
                          aria-label={`Delete ${img.concept}`}
                          data-testid={`delete-cached-${img.cache_key}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
